import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * v2 · 04 Buttons — 16/600, sentence case, radius 12.
 *
 * Primary is the inverse of the page — white on black in the dark
 * theme, black on white in light. No accent colour on buttons; pink
 * is earned by checks and stock only. Hover darkens the fill one
 * step (white → #E5E5E5, #1A1A1A → #242424) via color-mix so both
 * themes derive it from their own roles. The pill variant lives only
 * in nav.
 *
 * The v1 variant names stay so call sites keep working:
 *   primary → inverse fill (the money action)
 *   ink     → raised secondary ("Add to bag")
 *   outline → hairline box, quiet
 *   inline  → underlined minor action
 *   ghost   → icon-only chrome
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md',
    'font-sans font-semibold leading-none',
    'transition-colors duration-[120ms] ease-standard',
    'cursor-pointer select-none',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
    'disabled:pointer-events-none',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary — page-inverse fill. Hover darkens one step.
        primary: [
          'border-0 bg-ink text-ink-inverse',
          'hover:bg-[color-mix(in_srgb,var(--sss-ink),var(--sss-bg)_12%)]',
          'disabled:bg-surface-sunk disabled:text-ink-faint',
        ].join(' '),

        // Raised secondary — #1A1A1A with a hairline, rises on hover.
        ink: [
          'border border-[color:var(--sss-field-border)] bg-surface-sunk text-ink',
          'hover:bg-[color-mix(in_srgb,var(--sss-surface-sunk),var(--sss-ink)_6%)]',
          'disabled:border-transparent disabled:bg-surface-sunk disabled:text-ink-faint',
        ].join(' '),

        // Outline — hairline box for navigation-ish actions.
        outline: [
          'border border-hairline bg-transparent text-ink',
          'hover:bg-surface-sunk',
          'disabled:border-hairline disabled:bg-transparent disabled:text-ink-faint',
        ].join(' '),

        // Inline — quiet text action; brightens on hover.
        inline: [
          'border-0 bg-transparent p-0 font-semibold text-ink-muted',
          'hover:text-ink',
          'disabled:text-ink-faint',
        ].join(' '),

        // Icon-only chrome control — keeps a 44×44 tap box on mobile.
        ghost: [
          'border-0 bg-transparent text-ink',
          'hover:text-ink-muted',
          'disabled:text-ink-faint',
        ].join(' '),

        // Nav pill — the only pill-shaped button in the system.
        pill: [
          'rounded-full border-0 bg-ink text-ink-inverse',
          'hover:bg-[color-mix(in_srgb,var(--sss-ink),var(--sss-bg)_12%)]',
          'disabled:bg-surface-sunk disabled:text-ink-faint',
        ].join(' '),
      },
      size: {
        // 48 default
        default: 'h-12 px-6 text-[15px] [&_svg]:size-5',
        // 40 compact — also the nav pill height
        compact: 'h-10 px-5 text-[14px] [&_svg]:size-4',
        // 52 block · full width, checkout & configurator
        block: 'h-13 w-full px-4 text-[16px] [&_svg]:size-5',
        // Icon-only, square, centred
        icon: 'size-10 p-0 [&_svg]:size-5',
        // 44×44 tap box for mobile chrome
        tap: 'size-11 p-0 [&_svg]:size-[22px]',
        // Inline buttons have no box at all
        inline: 'h-auto p-0 text-[14px]',
      },
    },
    compoundVariants: [
      // Inline never takes a box height, whatever size is passed.
      { variant: 'inline', class: 'h-auto p-0' },
      // Pills keep their radius at any size.
      { variant: 'pill', class: 'rounded-full' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Processing state — sinks the fill to the raised gray and shows the dot. */
  processing?: boolean
}

/**
 * Processing dot: 8px, the one place pink appears on a button —
 * confirmation-in-progress. The label stays put so the button doesn't
 * reflow mid-submit.
 */
function ProcessingDot() {
  return <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-signal" />
}

const processingStyles: Record<string, string> = {
  primary:
    'bg-surface-sunk text-ink-muted hover:bg-surface-sunk hover:text-ink-muted',
  ink: 'border border-hairline bg-surface-sunk text-ink-muted hover:bg-surface-sunk hover:text-ink-muted',
  outline: 'border-hairline bg-surface-sunk text-ink-muted hover:bg-surface-sunk',
  inline: '',
  ghost: '',
  pill: 'bg-surface-sunk text-ink-muted hover:bg-surface-sunk',
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size, asChild = false, processing, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const key = variant ?? 'primary'

    const classes = cn(
      buttonVariants({ variant, size }),
      processing && processingStyles[key],
      disabled && 'cursor-not-allowed',
      className
    )

    // Slot demands exactly one child, so asChild renders the child
    // alone — a link that renders as a button has nothing to submit
    // and therefore no processing state to show.
    if (asChild) {
      return (
        <Comp className={classes} ref={ref} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={classes}
        ref={ref}
        disabled={disabled || processing}
        aria-busy={processing || undefined}
        {...props}
      >
        {processing ? (
          <>
            <ProcessingDot />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
